import React from 'react';
import type { MergePreview } from '../../services/api/domains/merge/types';
import { modalStyles as styles } from './styles';

interface ConflictField {
  fieldName: string;
  label: string;
  sourceValue: string | number | boolean | Date | null | undefined;
  targetValue: string | number | boolean | Date | null | undefined;
  hasConflict: boolean;
}

interface FacilityMergeConflictResolverProps {
  previewData: MergePreview;
  resolvedFields: Record<
    string,
    string | number | boolean | Date | null | undefined
  >;
  onFieldResolve: (
    fieldName: string,
    value: string | number | boolean | Date | null | undefined,
    source: 'source' | 'target'
  ) => void;
}

export const FacilityMergeConflictResolver: React.FC<
  FacilityMergeConflictResolverProps
> = ({ previewData, resolvedFields, onFieldResolve }) => {
  // Define all facility fields that can have conflicts
  const conflictableFields: Array<{
    key: string;
    label: string;
    formatter?: (value: string | number | boolean | Date | null) => string;
  }> = [
    { key: 'fei_number', label: 'FEI Number' },
    { key: 'duns_number', label: 'DUNS Number' },
    { key: 'address_line1', label: 'Address Line 1' },
    { key: 'address_line2', label: 'Address Line 2' },
    { key: 'city', label: 'City' },
    { key: 'state_province', label: 'State/Province' },
    { key: 'postal_code', label: 'Postal Code' },
    { key: 'country_code', label: 'Country Code' },
    { key: 'qta_status', label: 'QTA Status' },
    {
      key: 'qta_effective_date',
      label: 'QTA Effective Date',
      formatter: val => {
        if (!val || val === null || val === undefined) return 'N/A';
        if (typeof val === 'boolean') return 'N/A';
        return new Date(val).toLocaleDateString();
      },
    },
    {
      key: 'qta_expiration_date',
      label: 'QTA Expiration Date',
      formatter: val => {
        if (!val || val === null || val === undefined) return 'N/A';
        if (typeof val === 'boolean') return 'N/A';
        return new Date(val).toLocaleDateString();
      },
    },
    {
      key: 'latitude',
      label: 'Latitude',
      formatter: val =>
        val !== null && val !== undefined ? val.toString() : 'N/A',
    },
    {
      key: 'longitude',
      label: 'Longitude',
      formatter: val =>
        val !== null && val !== undefined ? val.toString() : 'N/A',
    },
    { key: 'facility_status', label: 'Facility Status' },
    {
      key: 'is_primary',
      label: 'Is Primary',
      formatter: val => (val === true ? 'Yes' : val === false ? 'No' : 'N/A'),
    },
  ];

  // Build conflict fields array
  const conflicts: ConflictField[] = conflictableFields
    .map(field => {
      const sourceValue = previewData[`source_${field.key}`];
      const targetValue = previewData[`target_${field.key}`];
      const hasConflict = previewData[`conflict_${field.key}`] === 1;

      return {
        fieldName: field.key,
        label: field.label,
        sourceValue,
        targetValue,
        hasConflict,
      };
    })
    .filter(conflict => conflict.hasConflict);

  // Dynamic styles as separate functions
  const getFieldOptionStyle = (
    isSelected: boolean,
    color: string,
    bgColor: string
  ): React.CSSProperties => ({
    display: 'flex',
    alignItems: 'flex-start',
    gap: '8px',
    padding: '8px',
    border: isSelected ? `2px solid ${color}` : '1px solid #e5e7eb',
    borderRadius: '6px',
    backgroundColor: isSelected ? bgColor : '#f9fafb',
    cursor: 'pointer',
    transition: 'all 0.2s',
  });

  const getFieldOptionTitleStyle = (color: string): React.CSSProperties => ({
    fontSize: '12px',
    fontWeight: '600',
    color,
    marginBottom: '4px',
  });

  if (conflicts.length === 0) {
    return (
      <div style={styles.noConflictsContainer}>
        <div style={styles.noConflictsTitle}>
          ✅ No field conflicts detected
        </div>
        <div style={styles.noConflictsDescription}>
          All conflicting fields have either the same value or one is empty.
        </div>
      </div>
    );
  }

  const formatValue = (
    value: string | number | boolean | Date | null | undefined,
    fieldKey: string
  ): string => {
    if (value === null || value === undefined || value === '') return 'N/A';

    const field = conflictableFields.find(f => f.key === fieldKey);
    if (field?.formatter) {
      return field.formatter(value);
    }

    return String(value);
  };

  return (
    <div style={styles.conflictContainer}>
      <h4 style={styles.conflictTitle}>
        ⚠️ Resolve Field Conflicts ({conflicts.length})
      </h4>
      <div style={styles.conflictDescription}>
        These fields have different values. Select which value to keep for each
        field.
      </div>

      <div style={styles.conflictFieldContainer}>
        {conflicts.map(conflict => {
          const selectedSource =
            resolvedFields[`resolved_${conflict.fieldName}`] ===
            conflict.sourceValue;
          const selectedTarget =
            resolvedFields[`resolved_${conflict.fieldName}`] ===
            conflict.targetValue;

          return (
            <div key={conflict.fieldName} style={styles.conflictField}>
              <div style={styles.fieldLabel}>{conflict.label}</div>

              <div style={styles.fieldOptionsContainer}>
                {/* Source Value */}
                <label
                  style={getFieldOptionStyle(
                    selectedSource,
                    '#f59e0b',
                    '#fffbeb'
                  )}>
                  <input
                    type="checkbox"
                    checked={selectedSource}
                    onChange={() =>
                      onFieldResolve(
                        conflict.fieldName,
                        conflict.sourceValue,
                        'source'
                      )
                    }
                    style={{ marginTop: '2px', cursor: 'pointer' }}
                  />
                  <div style={{ flex: 1 }}>
                    <div style={getFieldOptionTitleStyle('#92400e')}>
                      Source
                    </div>
                    <div style={styles.fieldOptionValue}>
                      {formatValue(conflict.sourceValue, conflict.fieldName)}
                    </div>
                  </div>
                </label>

                {/* Target Value */}
                <label
                  style={getFieldOptionStyle(
                    selectedTarget,
                    '#10b981',
                    '#ecfdf5'
                  )}>
                  <input
                    type="checkbox"
                    checked={selectedTarget}
                    onChange={() =>
                      onFieldResolve(
                        conflict.fieldName,
                        conflict.targetValue,
                        'target'
                      )
                    }
                    style={{ marginTop: '2px', cursor: 'pointer' }}
                  />
                  <div style={{ flex: 1 }}>
                    <div style={getFieldOptionTitleStyle('#065f46')}>
                      Target
                    </div>
                    <div style={styles.fieldOptionValue}>
                      {formatValue(conflict.targetValue, conflict.fieldName)}
                    </div>
                  </div>
                </label>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
